import Axios from "../api/axios";
import {
	getApiValueKeys,
	handleNestedKeys,
	findKeyInResponse,
	getParamString,
	removeSpaces,
	objectMapping,
	digiLockerReset
} from "./helper";
import { handleFieldSuggestionDependency } from "./dependencies";
import { processCtaClick } from "../native/layers";
import { analyticsProcessor, formProcessor } from "./onLoad";
import { getQueryParam } from "../native/utility";
import { getFromLocalStorage, setInLocalStorage } from "./utility";
import {
	ACTIONCTAERRORINFO,
	COMMONUSERBLOCKERRORINFO,
	SECTIONSTATES,
	cocpInstance,
	fieldActionConstants,
} from "../common/constants";
import { ClickStreamEventActions } from "../native/constants";
import { handleCommonErrorModal } from "./services";

const staticConfiguredTypes = {
	aadhaarcard: "aadhaarcard",
	digilocker: "digilocker",
	edit: "edit",
};

const processConfigKeysConditions = (entity) => {
	if (
		entity?.currentField?.value.toLowerCase() ==
		staticConfiguredTypes.digilocker.toLowerCase()
	) {
		return getQueryParam("state");
	} else return true;
};


const errorAnalyticsObject = (apiInfo, type = "") => {
	analyticsProcessor.createAndSendAnalytics({
		analyticsId: `${apiInfo?.id}error`,
		metric: {
			section: "apidetails",
			subSection: apiInfo?.id,
			action: ClickStreamEventActions.error,
			eventLabel: apiInfo?.id,
			eVar32: `${type} : ${apiInfo?.endPoint}`
		}
	});
}

const isAutoRetryCondition = (body) => {
	return body?.errorInfo?.state === ACTIONCTAERRORINFO.AUTORETRY ? true : false;
}

const processAPI = async (apiInfo, entity) => {
	if (apiInfo) {
		let retries = entity?.retries || -1;
		if (retries == -1) retries = (entity?.attributes?.retryConfig && entity?.attributes?.retryConfig[apiInfo?.id] + 1) || 1;
		const config = {
			method: apiInfo.method,
			url: apiInfo.endPoint,
		};
		config.headers = await processHeaders(
			apiInfo?.requestDetails?.headers,
			entity?.fieldData
		);
		config.data = await processBody(
			apiInfo?.requestDetails?.body,
			entity?.fieldData
		);
		config.requestParams = await processRequestParams(
			apiInfo?.requestDetails?.requestParams,
			entity?.fieldData
		);
		try {
			if (retries > 0) {
				const response = await Axios.request(config);
				if (response && response?.data && response?.data?.body) {
					if (
						!response?.data?.body ||
						Object.keys(response?.data?.body).length == 0
					) {
						return false;
					}
					const flag = processErrorInfoState(response?.data?.body, entity);
					if (flag) processFormAPIResponse(apiInfo, response?.data?.body, entity);
					else {
						if (isAutoRetryCondition(response?.data?.body)) {
							if (retries > 1) {
								entity[`retries`] = retries - 1;
								return new Promise((resolve) => {
									resolve(processAPI(apiInfo, entity))
								});
							}
							else {
								handleCommonErrorModal(COMMONUSERBLOCKERRORINFO);
								errorAnalyticsObject(apiInfo, "api data issue");
								return false;
							}
						}
						errorAnalyticsObject(apiInfo, "api data issue");
						return false;
					}
				}
				retries = 0;
				return response?.data;
			}
		} catch (e) {
			errorAnalyticsObject(apiInfo, "api data exception");
			console.error(e);
		}
		finally {
			formProcessor.updateLoaderState(entity?.sectionId, false);
		}
	}
	return false;
};

const processHeaders = async (headers, fieldData) => {
	const data = {};
	if (headers) {
		for (const entry of headers) {
			const initialValue = entry.value;
			const targetValue = await getApiValueKeys(initialValue, fieldData);
			data[entry.key] = targetValue;
		}
	}

	return data;
};

const processBody = async (body, fieldData) => {
	let data = {};
	if (body) {
		for (const entry of body) {
			const initialValue = entry.value;
			const targetValue = await getApiValueKeys(initialValue, fieldData);
			const allkeys = entry.key.split(".");
			if (allkeys.length === 1) {
				data[entry.key] = targetValue;
			} else {
				const key = allkeys.shift();
				if (!data[key]) data[key] = {};
				Object.assign(data[key], handleNestedKeys({}, allkeys, targetValue));
			}
		}
	}
	return data;
};

const processRequestParams = async (requestParams, fieldData) => {
	const data = {};
	if (requestParams) {
		for (const entry of requestParams) {
			const initialValue = entry.value;
			const targetValue = await getApiValueKeys(initialValue, fieldData);
			data[entry.key] = targetValue;
		}
	}
	return data;
};

const processFormAPIResponse = (apiInfo, body) => {
	handleFieldSuggestionDependency(body);
	processSectionDependency(body);
	processRedirectionDependency(body);
	processResponseDetailsNode(body, apiInfo);
	if (apiInfo && body?.analyticsObject) {
		analyticsProcessor.createAndSendAnalytics({
			analyticsId: `apiimpression`,
			metric: objectMapping({}, body?.analyticsObject?.metric)
		})
	}
};

const processRedirectionDependency = (body) => {
	if (body && body?.redirectionDependency) {
		processRedirection(body?.redirectionDependency?.redirectionInfo);
	}
}
const processSectionDependency = (body) => {
	if (body && body?.sectionDependency) {
		formProcessor.setSectionStateAndPreferredAction(body.sectionDependency);
	}
};

const processSectionActionLabel = (sectionActionLabel) => {
	if (sectionActionLabel) {
		const keyarr = Object.keys(sectionActionLabel);
		for (const section of keyarr) {
			formProcessor.setActionLabel(section, sectionActionLabel[section].value);
		}
	}
};

const processErrorInfoState = (body, entity) => {
	if (body?.errorInfo?.type) {
		body?.errorInfo && processErrorState(body?.errorInfo, entity);
		body?.errorInfo?.updateSectionDependency && processSectionDependency(body);
		body?.errorInfo?.updateFieldSuggestions && handleFieldSuggestionDependency(body);
		body?.errorInfo?.digiLockerReset && digiLockerReset();
		return false;
	}
	return true;
};

const processErrorState = (errorInfo, entity) => {
	switch (errorInfo?.state) {
		case ACTIONCTAERRORINFO.ERROR: {
			formProcessor.updateField({
				fieldId: errorInfo?.field || entity?.id,
				errorInfo: errorInfo,
			}, { actionType: fieldActionConstants.UPDATE_ERRORINFO });
			break;
		}
		case ACTIONCTAERRORINFO.MODAL: {
			handleCommonErrorModal(errorInfo);
			break;
		}
	}
}

const processResponseDetailsNode = (data, apiInfo) => {
	if (apiInfo?.responseDetails) {
		try {
			for (const resKey of apiInfo.responseDetails) {
				if (resKey.storage === "LOCAL") {
					const value = getFromLocalStorage("", resKey.key);
					value[resKey.getter] = findKeyInResponse(data, resKey.getter)[0];
					setInLocalStorage("", resKey.key, value);
				}
			}
		} catch (ex) {
			console.error(`processResponseDetailsNode`, ex);
		}
	}
};

const processRedirection = async (redirectionInfo, entity) => {
	localStorage.removeItem(cocpInstance);
	if (!redirectionInfo) return undefined;

	if (!redirectionInfo.queryParams) {
		return processCtaClick(
			{ deeplink: redirectionInfo.deeplink },
			entity?.navigate || entity?.currentField?.navigate
		);
	}

	const paramString = await getParamString(redirectionInfo?.queryParams);

	const redirectionDeeplink = {
		iuri: redirectionInfo.deeplink.iuri
			? `${redirectionInfo.deeplink.iuri}${paramString}`
			: "",
		tiuri: redirectionInfo.deeplink.tiuri
			? `${redirectionInfo.deeplink.tiuri}${paramString}`
			: "",
		turi: redirectionInfo.deeplink.turi
			? `${redirectionInfo.deeplink.turi}${paramString}`
			: "",
		uri: redirectionInfo.deeplink.uri
			? `${redirectionInfo.deeplink.uri}${paramString}`
			: "",
		wuri: redirectionInfo.deeplink.wuri
			? `${redirectionInfo.deeplink.wuri}${paramString}`
			: "",
	};
	processCtaClick(
		{ deeplink: redirectionDeeplink },
		entity?.navigate || entity?.currentField?.navigate
	);
};

const processAttributeHandler = async (entity) => {
	const _entity = JSON.parse(JSON.stringify(entity));
	let value =
		_entity?.currentField?.value || _entity?.currentField?.updatedValue;
	if (value) {
		formProcessor.updateLoaderState(_entity?.fieldData?.sectionId, false);
		value = typeof value == "object" ? value : removeSpaces(value).toLowerCase();
		switch (value) {
			case staticConfiguredTypes.aadhaarcard: {
				const response = await processBody(
					_entity.actionsMapping?.apiInfo?.requestDetails?.body,
					_entity.fieldData
				);
				handleFieldSuggestionDependency(
					response?.data[staticConfiguredTypes.aadhaarcard],
					{ flag: true, entity: entity }
				);
				response?.data[staticConfiguredTypes.aadhaarcard].sectionActionLabel && processSectionActionLabel(
					response?.data[staticConfiguredTypes.aadhaarcard].sectionActionLabel
				);
				break;
			}
			case staticConfiguredTypes.edit: {
				const response = await processBody(
					_entity.actionsMapping?.apiInfo?.requestDetails?.body,
					_entity.fieldData
				);
				handleFieldSuggestionDependency(
					response?.data[staticConfiguredTypes.edit],
					entity
				);
				formProcessor.displaySection(entity.fieldData.sectionId);
				break;
			}
			case staticConfiguredTypes.digilocker: {
				const response = await processBody(
					_entity.actionsMapping?.apiInfo?.requestDetails?.body,
					_entity.fieldData
				);
				handleFieldSuggestionDependency(
					response?.data[staticConfiguredTypes.digilocker],
					{ flag: true, entity: entity }
				);
				response?.data[staticConfiguredTypes.digilocker].sectionActionLabel && processSectionActionLabel(
					response?.data[staticConfiguredTypes.digilocker].sectionActionLabel
				);
				const process = processConfigKeysConditions(_entity);
				if (process) {
					formProcessor.updateLoaderState(_entity?.fieldData?.sectionId, true);
					await processAPI(
						_entity?.fieldData?.actionsMapping?.onClick?.apiInfo, _entity?.fieldData
					);
				} else {
					formProcessor.saveToValueFieldMap(_entity.fieldData.sectionId);
					formProcessor.saveValuesToLocalStorage();
					await processRedirection(
						response?.data[staticConfiguredTypes.digilocker].redirectionInfo
					);
				}
				break;
			}
			default: {
				const response = await processBody(
					_entity.actionsMapping?.apiInfo?.requestDetails?.body,
					_entity.fieldData
				);
				handleFieldSuggestionDependency(response?.data);
				break;
			}
		}
	}
};

const processFilterHandler = async (entity) => {
	const response = await processBody(
		entity.actionsMapping?.apiInfo?.requestDetails?.body,
		entity.fieldData
	);
	handleFieldSuggestionDependency(response?.data);
};

const processCollapseNext = (sectionId = null) => {
	const nextSection = formProcessor.getTheNextSection(
		sectionId || formProcessor.getActiveSection()
	);
	if (nextSection) {
		if (
			formProcessor.getSectionStatePrefferedAction(nextSection) !==
			SECTIONSTATES.HIDDEN
		)
			formProcessor.toggleBetweenSections(nextSection);
		else processCollapseNext(nextSection);
	}
};

export {
	processAPI,
	processBody,
	processRequestParams,
	processHeaders,
	processRedirection,
	processAttributeHandler,
	processFilterHandler,
	processCollapseNext,
	processErrorInfoState,
	processSectionActionLabel
};
